import { Events } from 'src/constants/webSocketConsts/events';
import { ApiTags, appAPI } from 'src/services/api';
import {
  Invoice,
  InvoiceLatestRefundStatus,
  invoicesApi,
  LoadInvoicesQueryOptions,
} from 'src/services/api/invoicesApi';
import {
  ListRefundsParams,
  Refund,
  refundsApi,
  RefundStatus,
} from 'src/services/api/refundsApi';
import { AppThunkDispatch, RootState } from 'src/store/reduxTypes';
import { updateListCache } from 'src/websocket/updateListCache';

const refundStatusToInvoiceLatestRefundStatus: Record<
  RefundStatus,
  InvoiceLatestRefundStatus
> = {
  [RefundStatus.Pending]: InvoiceLatestRefundStatus.Pending,
  [RefundStatus.Succeeded]: InvoiceLatestRefundStatus.Succeeded,
  [RefundStatus.Failed]: InvoiceLatestRefundStatus.Failed,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isOptionalString = (value: unknown): value is string | undefined =>
  value === undefined || typeof value === 'string';

const isOptionalBoolean = (value: unknown): value is boolean | undefined =>
  value === undefined || typeof value === 'boolean';

const isOptionalNumber = (value: unknown): value is number | undefined =>
  value === undefined || typeof value === 'number';

const isLoadInvoicesQueryOptions = (
  value: unknown,
): value is LoadInvoicesQueryOptions =>
  isRecord(value) &&
  isOptionalString(value.clientId) &&
  isOptionalBoolean(value.includeCompanyInvoices) &&
  isOptionalString(value.companyId) &&
  isOptionalString(value.subscriptionId);

const isListRefundsParams = (value: unknown): value is ListRefundsParams =>
  isRecord(value) &&
  typeof value.invoiceId === 'string' &&
  isOptionalNumber(value.limit) &&
  isOptionalString(value.nextToken);

const getRefundInvoiceId = (refund: Refund) => {
  if (refund.invoiceId) {
    return refund.invoiceId;
  }

  return refund.data?.split('/').at(1);
};

const updateInvoiceRefundStatus = (
  invoiceId: string,
  refundStatus: RefundStatus,
  draft?: Invoice[],
) => {
  if (!draft) {
    return;
  }

  const invoice = draft.find((item) => item.id === invoiceId);
  if (invoice) {
    invoice.latestRefundStatus =
      refundStatusToInvoiceLatestRefundStatus[refundStatus];
  }
};

const updateInvoiceDetailsRefundStatus = (
  refundStatus: RefundStatus,
  draft?: Invoice,
) => {
  if (!draft) {
    return;
  }

  draft.latestRefundStatus =
    refundStatusToInvoiceLatestRefundStatus[refundStatus];
};

const patchRefundQueries = (
  dispatch: AppThunkDispatch,
  state: RootState,
  event: Events,
  refund: Refund,
  invoiceId: string,
) => {
  appAPI.util
    .selectInvalidatedBy(state, [{ type: ApiTags.refunds }])
    .forEach((cacheEntry) => {
      if (
        cacheEntry.endpointName !== 'getRefundsByInvoiceId' ||
        !isListRefundsParams(cacheEntry.originalArgs) ||
        cacheEntry.originalArgs.invoiceId !== invoiceId
      ) {
        return;
      }

      dispatch(
        refundsApi.util.updateQueryData(
          'getRefundsByInvoiceId',
          cacheEntry.originalArgs,
          (draft?: Refund[]) => {
            updateListCache(event, [refund], draft, {
              insertMissingOnModify: true,
            });
          },
        ),
      );
    });
};

const patchInvoiceQueries = (
  dispatch: AppThunkDispatch,
  state: RootState,
  invoiceId: string,
  refundStatus: RefundStatus,
) => {
  appAPI.util
    .selectInvalidatedBy(state, [
      { type: ApiTags.invoices },
      { type: ApiTags.clientInvoices },
    ])
    .forEach((cacheEntry) => {
      switch (cacheEntry.endpointName) {
        case 'getInvoices':
          dispatch(
            invoicesApi.util.updateQueryData(
              'getInvoices',
              undefined,
              (draft?: Invoice[]) => {
                updateInvoiceRefundStatus(invoiceId, refundStatus, draft);
              },
            ),
          );
          return;
        case 'getInvoicesByRecipientId':
          if (!isLoadInvoicesQueryOptions(cacheEntry.originalArgs)) {
            return;
          }
          dispatch(
            invoicesApi.util.updateQueryData(
              'getInvoicesByRecipientId',
              cacheEntry.originalArgs,
              (draft?: Invoice[]) => {
                updateInvoiceRefundStatus(invoiceId, refundStatus, draft);
              },
            ),
          );
          return;
        case 'getInvoicesBySubscriptionId':
          if (typeof cacheEntry.originalArgs !== 'string') {
            return;
          }
          dispatch(
            invoicesApi.util.updateQueryData(
              'getInvoicesBySubscriptionId',
              cacheEntry.originalArgs,
              (draft?: Invoice[]) => {
                updateInvoiceRefundStatus(invoiceId, refundStatus, draft);
              },
            ),
          );
          return;
        case 'getInvoiceById':
          if (cacheEntry.originalArgs !== invoiceId) {
            return;
          }
          dispatch(
            invoicesApi.util.updateQueryData(
              'getInvoiceById',
              invoiceId,
              (draft?: Invoice) => {
                updateInvoiceDetailsRefundStatus(refundStatus, draft);
              },
            ),
          );
          return;
        default:
          return;
      }
    });
};

export function handleRefundUpdate(
  event: Events,
  items: Refund[],
  dispatch: AppThunkDispatch,
) {
  if (!items.length) {
    return;
  }

  dispatch((thunkDispatch, getState) => {
    const state = getState();

    items.forEach((refund) => {
      const invoiceId = getRefundInvoiceId(refund);
      if (!invoiceId) {
        return;
      }

      patchRefundQueries(thunkDispatch, state, event, refund, invoiceId);

      if (event === Events.Insert || event === Events.Modify) {
        patchInvoiceQueries(thunkDispatch, state, invoiceId, refund.status);
      }
    });

    // Keep active queries aligned with the backend for event types the local
    // cache patch cannot fully infer, such as removals or missing invoice ids.
    thunkDispatch(
      appAPI.util.invalidateTags([
        { type: ApiTags.refunds },
        { type: ApiTags.invoices },
        { type: ApiTags.clientInvoices },
      ]),
    );
  });
}
